import { connect, ConnectedProps } from 'react-redux';
import { reset } from 'redux-form';

import {
  FORM_ORGANIZATION_SESSION_LENGTH_NAME,
  FormOrganizationSessionLengthData,
} from 'dashboard/forms/FormOrganizationSessionLength';
import { FormSamlMetadataUrlData } from 'dashboard/forms/FormSamlMetadataUrl/FormSamlMetadataUrl';
import { FormSamlMetadataXmlData } from 'dashboard/forms/FormSamlMetadataXml/FormSamlMetadataXml';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import useTableState from 'dashboard/lib/hooks/useTableState';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Organization } from 'dashboard/models/organization';
import { MfaMethod } from 'dashboard/models/user';
import { clearMessages, showMessage } from 'dashboard/redux/actions/messages';
import { setOrganization } from 'dashboard/redux/actions/organization';
import { RootState } from 'dashboard/redux/store';
import {
  updateOrganization,
  updateSamlUrl,
  updateSamlXml,
} from 'dashboard/redux/thunk-actions/organization';
import { loadPaginatedUserListWithPermissionsContext } from 'dashboard/redux/thunk-actions/user';
import DashboardOrganizationSecurityView from 'dashboard/views/DashboardOrganizationSecurityView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { Iso8601DateString } from 'lib/types';

const DashboardOrganizationSecurityRoute = (props: PropsFromRedux) => {
  const { tableState, paginationMeta, setPaginationMeta } = useTableState({});
  const { handlePageLinkClick, handlePageSizeClick } = useTable(tableState);
  const { callback: updateSamlUrl, error: updateSamlUrlError } = useApiCallback(
    async ({ metadataUrl }: FormSamlMetadataUrlData) => {
      props.clearMessages();
      const oldMetadata = props.organization.attributes?.['idp-metadata'];
      await props.updateSamlUrl(metadataUrl);
      if (oldMetadata == null || Object.keys(oldMetadata).length === 0) {
        props.showMessage(
          'Registration successful! Your organization is now managed through SAML.',
          MessageType.Success,
        );
      } else {
        props.showMessage('Your SAML configuration has been updated.', MessageType.Success);
      }
    },
    [],
  );

  useApiErrorHandler({
    error: updateSamlUrlError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'SAML failed to update. Please check the metadata URL and try again.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: updateSamlXml, error: updateSamlXmlError } = useApiCallback(
    async ({ metadataXml }: FormSamlMetadataXmlData) => {
      props.clearMessages();
      const oldMetadata = props.organization.attributes?.['idp-metadata'];
      await props.updateSamlXml(metadataXml);
      if (oldMetadata == null || Object.keys(oldMetadata).length === 0) {
        props.showMessage(
          'Registration successful! Your organization is now managed through SAML.',
          MessageType.Success,
        );
      } else {
        props.showMessage('Your SAML configuration has been updated.', MessageType.Success);
      }
    },
    [],
  );

  useApiErrorHandler({
    error: updateSamlXmlError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'SAML failed to update. Please check the metadata XML and try again.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: loadPaginatedUserListWithPermissionsContext } = useApiLoader({
    loader: () =>
      props.loadPaginatedUserListWithPermissionsContext(
        tableState.currentPage,
        tableState.pageSize,
        setPaginationMeta,
      ),
    deps: [props.loadPaginatedUserListWithPermissionsContext],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load users.');
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const { callback: updateMfaRequired, error: updateMfaRequiredError } = useApiCallback(
    async (requireAt?: Iso8601DateString) => {
      props.clearMessages();

      if (requireAt == null) {
        await props.updateOrganization({ 'mfa-required-at': null });
        props.showMessage(
          '2-factor organization is no longer required for your organization.',
          MessageType.Success,
        );
      } else {
        await props.updateOrganization({ 'mfa-required-at': requireAt });
        props.showMessage(
          '2-factor organization is now required for your organization.',
          MessageType.Success,
        );
      }
    },
    [],
  );

  const { callback: updateSamlRequired, error: updateSamlRequiredError } = useApiCallback(
    async (requireAt?: Iso8601DateString) => {
      props.clearMessages();

      if (requireAt == null) {
        await props.updateOrganization({ 'saml-required-at': null });
        props.showMessage(
          'SAML authentication is no longer required for your organization.',
          MessageType.Success,
        );
      } else {
        await props.updateOrganization({ 'saml-required-at': requireAt });
        props.showMessage(
          'SAML authentication is now required for your organization.',
          MessageType.Success,
        );
      }
    },
    [],
  );

  const { callback: updateEnabledMfaMethods, error: updateEnabledMfaMethodsError } = useApiCallback(
    async (enabledMfaMethods: MfaMethod[]) => {
      props.clearMessages();
      await props.updateOrganization({ 'enabled-mfa-methods': enabledMfaMethods });

      // Refresh available-mfa-methods for each user
      await loadPaginatedUserListWithPermissionsContext();

      props.showMessage(
        'Your enabled 2-factor authentication methods have been updated.',
        MessageType.Success,
      );
    },
    [],
  );

  const updateOrganizationSessionLength = async (attributes: FormOrganizationSessionLengthData) => {
    props.clearMessages();
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (attributes['session-length-seconds'] < 60 * 5) {
      props.showMessage('Please specify a session length greater than 5 minutes');
      props.reset(FORM_ORGANIZATION_SESSION_LENGTH_NAME);
    } else {
      try {
        const { data: organization } =
          await SessionClientDashboard.updateResourceSingleton<Organization>({
            attributes,
            type: ApiModel.Organization as const,
          });
        props.setOrganization(organization);
        props.showMessage('Organization session length was updated', MessageType.Success);
      } catch (err) {
        props.showMessage('Please try again');
        props.reset(FORM_ORGANIZATION_SESSION_LENGTH_NAME);
      }
    }
  };

  const { callback: updateRefreshActiveSessions } = useApiCallback(
    async (refreshSessionOnActivity: boolean) => {
      props.clearMessages();
      await props.updateOrganization({ 'refresh-session-on-activity': refreshSessionOnActivity });

      props.showMessage(
        "Your organization's session auto-refresh settings have been updated.",
        MessageType.Success,
      );
    },
    [],
  );

  useApiErrorHandler({
    error: updateMfaRequiredError || updateEnabledMfaMethodsError,
    onError: (e) => {
      if (isPermissionsError(e)) {
        props.showMessage(permissionsErrorMessage(e), MessageType.Failure);
      } else {
        switch (e.errors?.[0].details) {
          case 'MFA cannot be required if no MFA methods are enabled.':
            props.showMessage('2FA cannot be required if no 2FA methods are enabled.');
            break;
          default:
            props.showMessage(
              'Sorry, something went wrong. Please try again later or contact support.',
            );
        }
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  useApiErrorHandler({
    error: updateSamlRequiredError,
    onError: (e) => {
      if (isPermissionsError(e)) {
        props.showMessage(permissionsErrorMessage(e), MessageType.Failure);
      } else {
        props.showMessage(
          'Sorry, something went wrong. Please try again later or contact support.',
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  return (
    <DashboardOrganizationSecurityView
      organization={props.organization}
      updateOrganizationSessionLength={updateOrganizationSessionLength}
      users={props.users}
      currentUser={props.currentUser}
      handleSamlParseUrl={updateSamlUrl}
      handleSamlParseXml={updateSamlXml}
      updateMfaRequired={updateMfaRequired}
      updateSamlRequired={updateSamlRequired}
      updateEnabledMfaMethods={updateEnabledMfaMethods}
      updateRefreshActiveSessions={updateRefreshActiveSessions}
      IAMpageSize={tableState.pageSize}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      IAMpaginationMeta={paginationMeta}
      handleIAMPageLinkClick={handlePageLinkClick}
      handleIAMPageSizeClick={handlePageSizeClick}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  organization: state.organization.organization,
  users: state.user.users,
  currentUser: state.currentUser.currentUser,
});

const mapDispatchToProps = {
  setOrganization,
  loadPaginatedUserListWithPermissionsContext,
  updateOrganization,
  updateSamlUrl,
  updateSamlXml,
  clearMessages,
  showMessage,
  reset,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardOrganizationSecurityRoute);
